import React, { useMemo, useState } from 'react';
import { CreatableReactSelectMenu } from '../../../../components/Dropdown/StyledReactSelect';
import { ISelectableFieldItem, useAvailableFieldItemsToExtend } from './utils';
import { ICdwTableColumnData } from '../DataModelerCdwTableGrid/types';
import {
  components,
  MenuPlacement,
  SingleValue,
  SingleValueProps,
} from 'react-select';
import { colors } from '../../../../config/styles';
import styled from 'styled-components';
import { FieldClass } from '../../../../utils/apollo/graphqlSchema';
import { ControlWithSearchIcon } from '../../../../components/Dropdown/StyledReactSelect/SelectUtils';
import KubitTooltip from '../../../../components/KubitTooltip';

interface IProps {
  tableKey: string;
  columnData: ICdwTableColumnData;
  onChange: (selectedItem: ISelectableFieldItem) => void;
  onNewFieldName: (fieldName: string) => void;
  isRequired: boolean;
  menuPlacement: MenuPlacement;
}

const FieldSelectLabelDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const FieldSelectNameDiv = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 190px;
`;

const FieldSelectUnionNewDiv = styled.div``;

const FieldSelectLabel = (
  props: SingleValueProps<ISelectableFieldItem, false>,
) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <FieldSelectLabelDiv>
        <KubitTooltip
          content={
            data?.fieldName && data?.fieldName.length > 16
              ? data.fieldName
              : null
          }
          noOpenDelay={true}
        >
          <FieldSelectNameDiv>{data?.fieldName}</FieldSelectNameDiv>
        </KubitTooltip>
        <FieldSelectUnionNewDiv>
          {data.justCreated ? ' (new)' : ' (union)'}
        </FieldSelectUnionNewDiv>
      </FieldSelectLabelDiv>
    </components.SingleValue>
  );
};

const DataModelerSelectViewExtendFieldKey = ({
  tableKey,
  columnData,
  onChange,
  onNewFieldName,
  isRequired,
  menuPlacement,
}: IProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { current: currentValue, items: selectableItems } =
    useAvailableFieldItemsToExtend({
      tableKey,
      columnData,
    });

  const filteredItems = useMemo((): ISelectableFieldItem[] => {
    return [
      ...selectableItems.filter((item) =>
        item.fieldName.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    ];
  }, [searchTerm, selectableItems]);

  const handleChange = (newSelection: SingleValue<ISelectableFieldItem>) => {
    if (!newSelection) {
      return;
    }
    onChange(newSelection);
  };

  const handleValidateNewOption = (inputValue: string): boolean => {
    return (
      inputValue.trim().length > 0 &&
      !selectableItems.some((item) => item.fieldName === inputValue)
    );
  };

  const getNewOptionData = (newTerm: string): ISelectableFieldItem => {
    return {
      fieldClass:
        columnData.addFieldInput?.typeInput?.fieldClass ??
        columnData.extendFieldInput?.typeInput?.fieldClass ??
        FieldClass.Basic,
      fieldKey:
        columnData.addFieldInput?.key ??
        columnData.extendFieldInput?.fieldKey ??
        '',
      fieldName: newTerm,
      justCreated: true,
      type: columnData.type,
      viewColumn: columnData.addFieldInput?.viewColumn ?? columnData.column,
    };
  };

  const handleNewFieldNameChange = (newFieldName: string) => {
    onNewFieldName(newFieldName);
  };

  return (
    <CreatableReactSelectMenu
      isDisabled={isRequired && !!currentValue}
      isMulti={false}
      options={filteredItems}
      getOptionLabel={(option) => option.fieldName}
      getOptionValue={(option) => option.fieldKey}
      placeholder={'Select Field'}
      value={currentValue}
      onChange={handleChange}
      // Creatable special properties
      isValidNewOption={handleValidateNewOption}
      getNewOptionData={getNewOptionData}
      onCreateOption={handleNewFieldNameChange}
      allowCreateWhileLoading={true}
      isClearable={true}
      createOptionPosition="first"
      menuPlacement={menuPlacement}
      maxMenuHeight={120}
      components={{
        SingleValue: FieldSelectLabel,
        Option: FieldSelectLabel,
        Control: ControlWithSearchIcon,
      }}
      inputValue={searchTerm}
      onInputChange={setSearchTerm}
      formatCreateLabel={(inputValue: string) => (
        <div>
          <b>New field: </b>
          <span>{inputValue}</span>
        </div>
      )}
      styles={{
        control: (base, _) => {
          return {
            ...base,
            width: '100%',
            borderColor: !currentValue ? colors.mainError : base.borderColor,
          };
        },
        menu: (base, _) => {
          return {
            ...base,
            width: '100%',
          };
        },
      }}
    />
  );
};

export default DataModelerSelectViewExtendFieldKey;
